import React, { useState, useEffect, useMemo } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type { GRN, Product } from '@/types/pos'

interface VolumePriceRule {
  blockSize: number
  pricePerUnit: number
}

interface GRNFormItem {
  productId: string
  productName: string
  sku: string
  barcode: string
  quantity: number
  unitCost: number
  sellingPrice: number
  wholesalePrice: number
  discountPrice: number
  markedPrice: number
  volumePriceRules: VolumePriceRule[]
  volumeDiscountThreshold: number
  volumeDiscountPrice: number
  expiryDate: string
  notes: string
  receivingUnitId?: string    // Sales unit used for receiving
  receivingUnitFactor?: number // Conversion factor
}

interface GRNPageProps {
  /** When true, hide the page title (e.g. when embedded in Procurement). */
  embedded?: boolean
}

export const GRNPage: React.FC<GRNPageProps> = ({ embedded = false }) => {
  const {
    grns,
    products,
    suppliers,
    isLoading,
    fetchGRNs,
    fetchSuppliers,
    createGRN,
    updateGRN,
    deleteGRN
  } = usePOSStore()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [viewingGRN, setViewingGRN] = useState<GRN | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // View modal edit state
  const [editingPaymentStatus, setEditingPaymentStatus] = useState<'pending' | 'partial' | 'paid'>('pending')
  const [editingNotes, setEditingNotes] = useState('')
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  // Form state
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'partial' | 'paid'>('pending')
  const [grnNotes, setGrnNotes] = useState('')
  const [grnItems, setGrnItems] = useState<GRNFormItem[]>([
    { productId: '', productName: '', sku: '', barcode: '', quantity: 1, unitCost: 0, sellingPrice: 0, wholesalePrice: 0, discountPrice: 0, markedPrice: 0, volumePriceRules: [], volumeDiscountThreshold: 0, volumeDiscountPrice: 0, expiryDate: '', notes: '' }
  ])
  const [expandedPricingRows, setExpandedPricingRows] = useState<Set<number>>(new Set())
  // Per-item product search state (indexed by item row)
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null)
  const [productSearch, setProductSearch] = useState('')
  // Track the full selected product object per row for UoM support
  const [selectedProductsPerRow, setSelectedProductsPerRow] = useState<(Product | null)[]>([null])

  const { showSuccess, showError } = useNotificationActions()

  useEffect(() => {
    fetchGRNs()
    fetchSuppliers()
  }, [fetchGRNs, fetchSuppliers])

  const filteredGRNs = grns.filter(grn => {
    const search = searchTerm.toLowerCase()
    return (
      (grn.grnNumber || '').toLowerCase().includes(search) ||
      (grn.supplierName || '').toLowerCase().includes(search) ||
      (grn.invoiceNumber || '').toLowerCase().includes(search)
    )
  })

  const totalGRNValue = grns.reduce((sum, g) => sum + (g.totalCost || 0), 0)

  const getPaymentColor = (status: string) => {
    switch (status) {
      case 'paid': return 'text-green-400 bg-green-400/10 border-green-400/20'
      case 'partial': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20'
      default: return 'text-red-400 bg-red-400/10 border-red-400/20'
    }
  }

  const emptyGRNItem = (): GRNFormItem => ({
    productId: '', productName: '', sku: '', barcode: '', quantity: 1, unitCost: 0,
    sellingPrice: 0, wholesalePrice: 0, discountPrice: 0, markedPrice: 0,
    volumePriceRules: [], volumeDiscountThreshold: 0, volumeDiscountPrice: 0,
    expiryDate: '', notes: ''
  })

  const handleAddItem = () => {
    setGrnItems([...grnItems, emptyGRNItem()])
    setSelectedProductsPerRow([...selectedProductsPerRow, null])
  }

  const handleRemoveItem = (index: number) => {
    if (grnItems.length === 1) return
    setGrnItems(grnItems.filter((_, i) => i !== index))
    setSelectedProductsPerRow(selectedProductsPerRow.filter((_, i) => i !== index))
  }

  const handleItemChange = (index: number, field: keyof GRNFormItem, value: string | number) => {
    const updated = [...grnItems]
    updated[index] = { ...updated[index], [field]: value }
    setGrnItems(updated)
  }

  const handleSelectProduct = (index: number, product: Product) => {
    const updated = [...grnItems]
    // If product has salesUnits, pre-select the default one
    const defaultUnit = product.salesUnits?.find(su => su.isDefault) || product.salesUnits?.[0]
    updated[index] = {
      ...updated[index],
      productId: product.id || (product as any)._id,
      productName: product.name,
      sku: product.sku || '',
      barcode: product.barcode || product.isbn || '',
      unitCost: product.cost || 0,
      sellingPrice: defaultUnit ? defaultUnit.price : (product.price || 0),
      wholesalePrice: defaultUnit?.wholesalePrice ?? product.wholesalePrice ?? 0,
      receivingUnitId: defaultUnit?.id,
      receivingUnitFactor: defaultUnit?.conversionFactor,
    }
    setGrnItems(updated)
    // Track the selected product object for this row
    const updatedProductsPerRow = [...selectedProductsPerRow]
    updatedProductsPerRow[index] = product
    setSelectedProductsPerRow(updatedProductsPerRow)
    setProductSearch('')
    setActiveSearchIndex(null)
  }

  // Open a GRN in view/edit mode
  const handleViewGRN = (grn: GRN) => {
    setViewingGRN(grn)
    setEditingPaymentStatus(grn.paymentStatus as 'pending' | 'partial' | 'paid')
    setEditingNotes(grn.notes || '')
    setEditingInvoiceNumber(grn.invoiceNumber || '')
  }

  // Save payment status & notes changes
  const handleUpdateGRN = async () => {
    if (!viewingGRN) return
    const id = viewingGRN.id || viewingGRN._id
    if (!id) return

    const hasChanges =
      editingPaymentStatus !== viewingGRN.paymentStatus ||
      editingNotes !== (viewingGRN.notes || '') ||
      editingInvoiceNumber !== (viewingGRN.invoiceNumber || '')

    if (!hasChanges) {
      showSuccess('No Changes', 'Nothing was modified.')
      return
    }

    setIsUpdating(true)
    try {
      await updateGRN(id, {
        paymentStatus: editingPaymentStatus,
        notes: editingNotes || undefined,
        invoiceNumber: editingInvoiceNumber || undefined
      } as any)
      // Update local viewingGRN so the modal reflects the change immediately
      setViewingGRN(prev => prev ? {
        ...prev,
        paymentStatus: editingPaymentStatus,
        notes: editingNotes,
        invoiceNumber: editingInvoiceNumber
      } : null)
      showSuccess('GRN Updated', 'Payment status and details have been saved.')
    } catch (error) {
      showError('Update Failed', 'Could not update the GRN. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const totalFormCost = grnItems.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0)
  const totalFormItems = grnItems.reduce((sum, item) => sum + item.quantity, 0)

  const handleCreateGRN = async () => {
    const validItems = grnItems.filter(item => item.productName || item.productId)
    if (validItems.length === 0) {
      showError('Validation Error', 'Add at least one item to the GRN.')
      return
    }

    const supplier = suppliers.find(s => (s.id || s._id) === selectedSupplierId)

    setIsSubmitting(true)
    try {
      await createGRN({
        supplierId: selectedSupplierId || undefined,
        supplierName: supplier?.name || 'Walk-in Supplier',
        items: validItems as any,
        invoiceNumber: invoiceNumber || undefined,
        invoiceDate: invoiceDate || undefined,
        paymentStatus,
        notes: grnNotes || undefined
      })
      showSuccess('GRN Created', 'Goods received and stock has been updated.')
      resetForm()
      setShowCreateForm(false)
    } catch (error: any) {
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')
      const msg = isTimeout
        ? 'The server is taking too long. Please check your connection and try again.'
        : error?.response?.data?.message || error?.message || 'Could not create the GRN. Please try again.'
      showError('GRN Failed', msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setSelectedSupplierId('')
    setInvoiceNumber('')
    setInvoiceDate('')
    setPaymentStatus('pending')
    setGrnNotes('')
    setGrnItems([emptyGRNItem()])
    setSelectedProductsPerRow([null])
    setExpandedPricingRows(new Set())
  }

  const handleDeleteGRN = async (grn: GRN) => {
    if (!confirm(`Delete GRN ${grn.grnNumber}? Stock will be reversed for any remaining batch quantities.`)) return
    try {
      const id = grn.id || grn._id
      await deleteGRN(id!)
      showSuccess('GRN Deleted', 'The GRN record has been removed.')
    } catch (error) {
      showError('Delete Failed', 'Could not delete GRN.')
    }
  }

  // Filtered products for search dropdown (only master products — exclude expanded UoM virtual cards)
  const searchedProducts = useMemo(() => {
    if (!productSearch || activeSearchIndex === null) return []
    const s = productSearch.toLowerCase()
    return products
      .filter(p => !(p as any)._salesUnit)  // exclude expanded virtual cards
      .filter(p =>
        (p.name || '').toLowerCase().includes(s) ||
        (p.sku || '').toLowerCase().includes(s) ||
        (p.barcode || '').toLowerCase().includes(s)
      ).slice(0, 8)
  }, [productSearch, products, activeSearchIndex])

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          {!embedded && (
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Goods Received Notes</h1>
              <p className="text-muted-foreground mt-2">Receive stock from suppliers and track purchases.</p>
            </div>
          )}
          <button
            onClick={() => setShowCreateForm(true)}
            className="glow-button"
          >
            <span className="mr-2">📦</span> New GRN
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total GRNs', value: grns.length, icon: '📋', color: 'text-primary' },
            { label: 'Total Value', value: `Rs.${totalGRNValue.toLocaleString()}`, icon: '💰', color: 'text-green-400' },
            { label: 'Pending Payment', value: grns.filter(g => g.paymentStatus === 'pending').length, icon: '⏳', color: 'text-yellow-400' },
            { label: 'This Month', value: grns.filter(g => { const d = new Date(g.createdAt as string); const now = new Date(); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).length, icon: '📅', color: 'text-blue-400' }
          ].map((stat, i) => (
            <div key={i} className="glass-card">
              <div className="flex justify-between items-start mb-2">
                <span className="text-2xl">{stat.icon}</span>
                <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold font-sans">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="glass-card mb-6">
          <input
            type="text"
            placeholder="Search GRNs by number, supplier, or invoice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
          />
        </div>

        {/* GRN List */}
        {isLoading && grns.length === 0 ? (
          <div className="glass-card">
            <LoadingSpinner size="md" message="Loading GRNs" submessage="Syncing records..." />
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="md:hidden space-y-3">
              {filteredGRNs.length === 0 ? (
                <div className="glass-card py-12 text-center text-muted-foreground italic">
                  {searchTerm ? 'No GRNs match your search.' : 'No GRNs created yet. Click "New GRN" to receive goods.'}
                </div>
              ) : (
                filteredGRNs.map(grn => (
                  <div key={grn.id || grn._id} className="glass-card p-4 space-y-3">
                    {/* Top row: GRN number + status badge */}
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-bold text-primary">{grn.grnNumber}</span>
                      <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${getPaymentColor(grn.paymentStatus)}`}>
                        {grn.paymentStatus}
                      </span>
                    </div>
                    {/* Supplier + date */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{grn.supplierName || '—'}</span>
                      <span className="text-muted-foreground text-xs">
                        {grn.createdAt ? new Date(grn.createdAt as string).toLocaleDateString() : '—'}
                      </span>
                    </div>
                    {/* Total amount */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Total</span>
                      <span className="font-bold text-primary">Rs.{(grn.totalCost || 0).toLocaleString()}</span>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2 pt-1 border-t border-border/50">
                      <button
                        onClick={() => handleViewGRN(grn)}
                        className="flex-1 py-2 text-xs font-bold text-blue-400 bg-blue-400/10 hover:bg-blue-400/20 rounded-lg transition-colors"
                      >
                        VIEW
                      </button>
                      <button
                        onClick={() => handleDeleteGRN(grn)}
                        className="flex-1 py-2 text-xs font-bold text-red-400 bg-red-400/10 hover:bg-red-400/20 rounded-lg transition-colors"
                      >
                        DELETE
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-widest">
                      <th className="px-6 py-4 font-bold">GRN Number</th>
                      <th className="px-6 py-4 font-bold">Supplier</th>
                      <th className="px-6 py-4 font-bold">Invoice</th>
                      <th className="px-6 py-4 font-bold text-center">Items</th>
                      <th className="px-6 py-4 font-bold">Total Cost</th>
                      <th className="px-6 py-4 font-bold">Payment</th>
                      <th className="px-6 py-4 font-bold">Date</th>
                      <th className="px-6 py-4 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {filteredGRNs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-muted-foreground italic">
                          {searchTerm ? 'No GRNs match your search.' : 'No GRNs created yet. Click "New GRN" to receive goods.'}
                        </td>
                      </tr>
                    ) : (
                      filteredGRNs.map(grn => (
                        <tr key={grn.id || grn._id} className="hover:bg-muted/10 transition-colors">
                          <td className="px-6 py-4">
                            <span className="font-mono text-sm font-bold text-primary">{grn.grnNumber}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium">{grn.supplierName || '—'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm">{grn.invoiceNumber || '—'}</span>
                            {grn.invoiceDate && <p className="text-xs text-muted-foreground">{new Date(grn.invoiceDate).toLocaleDateString()}</p>}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="bg-muted px-2 py-1 rounded text-xs font-mono">{grn.totalItems || grn.items?.length || 0}</span>
                          </td>
                          <td className="px-6 py-4 font-bold text-primary">
                            Rs.{(grn.totalCost || 0).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${getPaymentColor(grn.paymentStatus)}`}>
                              {grn.paymentStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm">{grn.createdAt ? new Date(grn.createdAt as string).toLocaleDateString() : '—'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <button onClick={() => handleViewGRN(grn)} className="text-xs font-bold text-blue-400 hover:underline">VIEW</button>
                              <button onClick={() => handleDeleteGRN(grn)} className="text-xs font-bold text-red-400 hover:underline">DELETE</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Create GRN Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-4xl border-primary/30 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold esales-text mb-6">Create New GRN</h2>

              {/* Supplier & Invoice Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Supplier *</label>
                  <select
                    value={selectedSupplierId}
                    onChange={(e) => setSelectedSupplierId(e.target.value)}
                    className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.filter(s => s.isActive).map(s => (
                      <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Invoice Number</label>
                  <input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="INV-001"
                    className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Payment Status</label>
                  <select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value as any)}
                    className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="pending">Pending</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* Items */}
              <div className="mb-4">
                <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Items</p>

                <div className="space-y-3">
                  {grnItems.map((item, index) => (
                    <div key={index} className="p-4 bg-muted/20 border border-border/50 rounded-xl">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                        {/* Product selector */}
                        <div className="md:col-span-3 relative">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Product</label>
                          <input
                            value={activeSearchIndex === index && !item.productId ? productSearch : item.productName}
                            onChange={(e) => {
                              handleItemChange(index, 'productName', e.target.value)
                              // Clear productId when user starts typing again
                              if (item.productId) {
                                handleItemChange(index, 'productId', '')
                              }
                              setProductSearch(e.target.value)
                              setActiveSearchIndex(index)
                            }}
                            onFocus={() => {
                              setActiveSearchIndex(index)
                              if (item.productName && !item.productId) {
                                setProductSearch(item.productName)
                              }
                            }}
                            onBlur={() => {
                              // Small delay to allow click on dropdown items
                              setTimeout(() => {
                                if (activeSearchIndex === index) {
                                  setActiveSearchIndex(null)
                                  setProductSearch('')
                                }
                              }, 200)
                            }}
                            placeholder="Search product..."
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                          />
                          {activeSearchIndex === index && productSearch && searchedProducts.length > 0 && !item.productId && (
                            <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-xl max-h-40 overflow-y-auto">
                              {searchedProducts.map(p => (
                                <button
                                  key={p.id || (p as any)._id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleSelectProduct(index, p)}
                                  className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors text-sm"
                                >
                                  <span className="font-medium">{p.name}</span>
                                  <span className="text-muted-foreground ml-2 text-xs">{p.sku}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="md:col-span-1">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">
                            {item.receivingUnitId ? `Qty (${item.receivingUnitId})` : 'Qty'}
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                          />
                          {item.receivingUnitFactor && item.receivingUnitFactor > 1 && (
                            <p className="text-[9px] text-primary mt-0.5">
                              = {(item.quantity * item.receivingUnitFactor).toLocaleString()} {(selectedProductsPerRow[index] as any)?.baseUnit || 'units'}
                            </p>
                          )}
                        </div>

                        {/* Receiving Unit dropdown (only for UoM products) */}
                        {selectedProductsPerRow[index]?.salesUnits && selectedProductsPerRow[index]!.salesUnits!.length > 0 && (
                          <div className="md:col-span-1">
                            <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Recv. Unit</label>
                            <select
                              value={item.receivingUnitId || ''}
                              onChange={(e) => {
                                const su = selectedProductsPerRow[index]?.salesUnits?.find(u => u.id === e.target.value)
                                const updatedItems = [...grnItems]
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  receivingUnitId: su?.id,
                                  receivingUnitFactor: su?.conversionFactor,
                                  sellingPrice: su?.price ?? updatedItems[index].sellingPrice,
                                  wholesalePrice: (su?.wholesalePrice ?? updatedItems[index].wholesalePrice) || 0,
                                }
                                setGrnItems(updatedItems)
                              }}
                              className="w-full mt-1 px-2 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                            >
                              {selectedProductsPerRow[index]!.salesUnits!.map(su => (
                                <option key={su.id} value={su.id}>{su.name} (×{su.conversionFactor})</option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Unit Cost</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitCost || ''}
                            onChange={(e) => handleItemChange(index, 'unitCost', parseFloat(e.target.value) || 0)}
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Selling Price</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.sellingPrice || ''}
                            onChange={(e) => handleItemChange(index, 'sellingPrice', parseFloat(e.target.value) || 0)}
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                            placeholder="Retail price"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Wholesale Price</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.wholesalePrice || ''}
                            onChange={(e) => handleItemChange(index, 'wholesalePrice', parseFloat(e.target.value) || 0)}
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                            placeholder="Wholesale price"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Expiry</label>
                          <input
                            type="date"
                            value={item.expiryDate}
                            onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                            className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                          />
                        </div>

                        <div className="md:col-span-1 flex items-end gap-2">
                          <span className="text-sm font-bold text-primary py-2">
                            Rs.{(item.quantity * item.unitCost).toLocaleString()}
                          </span>
                        </div>

                        <div className="md:col-span-1 flex items-end">
                          {grnItems.length > 1 && (
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="px-2 py-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors text-xs"
                            >
                              ✕
                            </button>
                          )}
                        </div>

                        {/* Advanced Pricing toggle */}
                        <div className="md:col-span-12">
                          <button
                            type="button"
                            onClick={() => {
                              setExpandedPricingRows(prev => {
                                const next = new Set(prev)
                                next.has(index) ? next.delete(index) : next.add(index)
                                return next
                              })
                            }}
                            className="text-[10px] font-bold text-primary/70 hover:text-primary flex items-center gap-1 transition-colors"
                          >
                            {expandedPricingRows.has(index) ? '▲' : '▼'} Advanced Pricing
                          </button>
                        </div>
                      </div>

                      {/* Advanced Pricing Panel */}
                      {expandedPricingRows.has(index) && (
                        <div className="mt-3 pt-3 border-t border-border/50 space-y-4">
                          {/* Discount & Marked Price */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Discount Price (Rs.)</label>
                              <p className="text-[9px] text-muted-foreground">Promo price — applies to Regular &amp; Special customers</p>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.discountPrice || ''}
                                onChange={(e) => handleItemChange(index, 'discountPrice', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                                className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Marked Price (Rs.)</label>
                              <p className="text-[9px] text-muted-foreground">Manufacturer / company issued price</p>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.markedPrice || ''}
                                onChange={(e) => handleItemChange(index, 'markedPrice', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                                className="w-full mt-1 px-3 py-2 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                              />
                            </div>
                          </div>

                          {/* Volume Block Rules (rotation) */}
                          <div>
                            <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Volume Block Pricing (Rotation)</p>
                            <p className="text-[9px] text-muted-foreground mb-2">Every N units at a special rate; remainder at retail. e.g. every 500 @ Rs.2.50.</p>
                            <div className="space-y-2">
                              {item.volumePriceRules.map((rule, rIdx) => (
                                <div key={rIdx} className="flex gap-2 items-end">
                                  <div className="flex-1">
                                    <span className="text-[10px] text-muted-foreground">Block size (qty)</span>
                                    <input
                                      type="number"
                                      min={1}
                                      value={rule.blockSize || ''}
                                      onChange={(e) => {
                                        const rules = [...item.volumePriceRules]
                                        rules[rIdx] = { ...rules[rIdx], blockSize: parseInt(e.target.value) || 1 }
                                        handleItemChange(index, 'volumePriceRules', rules as any)
                                      }}
                                      placeholder="500"
                                      className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <span className="text-[10px] text-muted-foreground">Price/unit (Rs.)</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min={0}
                                      value={rule.pricePerUnit ?? ''}
                                      onChange={(e) => {
                                        const rules = [...item.volumePriceRules]
                                        rules[rIdx] = { ...rules[rIdx], pricePerUnit: parseFloat(e.target.value) || 0 }
                                        handleItemChange(index, 'volumePriceRules', rules as any)
                                      }}
                                      placeholder="2.50"
                                      className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const rules = item.volumePriceRules.filter((_, i) => i !== rIdx)
                                      handleItemChange(index, 'volumePriceRules', rules as any)
                                    }}
                                    className="pb-0.5 p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                    title="Remove rule"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const rules = [...item.volumePriceRules, { blockSize: 1, pricePerUnit: 0 }]
                                  handleItemChange(index, 'volumePriceRules', rules as any)
                                }}
                                className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                              >
                                + Add Rule
                              </button>
                            </div>
                          </div>

                          {/* Volume Discount (threshold) */}
                          <div>
                            <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Volume Discount (Threshold)</p>
                            <p className="text-[9px] text-muted-foreground mb-2">If qty ≥ threshold, ALL units at discount price. e.g. buy 50+, each @ Rs.8.</p>
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <span className="text-[10px] text-muted-foreground">Min qty threshold</span>
                                <input
                                  type="number"
                                  min={0}
                                  value={item.volumeDiscountThreshold || ''}
                                  onChange={(e) => handleItemChange(index, 'volumeDiscountThreshold', parseInt(e.target.value) || 0)}
                                  placeholder="e.g. 50"
                                  className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                />
                              </div>
                              <div className="flex-1">
                                <span className="text-[10px] text-muted-foreground">Price/unit at threshold (Rs.)</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  value={item.volumeDiscountPrice || ''}
                                  onChange={(e) => handleItemChange(index, 'volumeDiscountPrice', parseFloat(e.target.value) || 0)}
                                  placeholder="e.g. 8.00"
                                  className="w-full mt-0.5 px-3 py-2 bg-background border border-border rounded-lg text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Item button — always below the last item */}
                  <button
                    onClick={handleAddItem}
                    className="w-full py-3 border-2 border-dashed border-primary/30 hover:border-primary/60 bg-primary/5 hover:bg-primary/10 text-primary rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="text-lg leading-none">+</span> Add Item
                  </button>
                </div>
              </div>

              {/* Totals */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm text-muted-foreground">Total Items: </span>
                    <span className="font-bold text-foreground">{totalFormItems}</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Total Cost: </span>
                    <span className="text-xl font-bold text-primary">Rs.{totalFormCost.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Notes</label>
                <textarea
                  value={grnNotes}
                  onChange={(e) => setGrnNotes(e.target.value)}
                  placeholder="Any additional notes about this delivery..."
                  rows={2}
                  className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => { resetForm(); setShowCreateForm(false); }}
                  className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGRN}
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'PROCESSING...' : 'RECEIVE GOODS'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View GRN Modal */}
        {viewingGRN && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="glass-card w-full max-w-3xl border-primary/30 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold esales-text">{viewingGRN.grnNumber}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Received on {viewingGRN.createdAt ? new Date(viewingGRN.createdAt as string).toLocaleDateString() : '—'}
                    {viewingGRN.receivedByName && ` by ${viewingGRN.receivedByName}`}
                  </p>
                </div>
                <button
                  onClick={() => setViewingGRN(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none p-1"
                >
                  ✕
                </button>
              </div>

              {/* Supplier & Invoice Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Supplier</p>
                  <p className="text-sm font-medium mt-1">{viewingGRN.supplierName || '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Invoice #</p>
                  <input
                    value={editingInvoiceNumber}
                    onChange={(e) => setEditingInvoiceNumber(e.target.value)}
                    placeholder="—"
                    className="w-full mt-1 px-3 py-1.5 bg-background/50 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Total Items</p>
                  <p className="text-sm font-bold text-primary mt-1">{viewingGRN.totalItems}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Total Cost</p>
                  <p className="text-sm font-bold text-green-400 mt-1">Rs.{(viewingGRN.totalCost || 0).toLocaleString()}</p>
                </div>
              </div>

              {/* Payment Status Update */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl mb-6">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-3">Payment Status</p>
                <div className="flex gap-3">
                  {(['pending', 'partial', 'paid'] as const).map(status => {
                    const isSelected = editingPaymentStatus === status
                    const colorMap = {
                      pending: isSelected ? 'bg-red-500/20 border-red-400 text-red-400 ring-2 ring-red-400/30' : 'bg-muted/30 border-border text-muted-foreground hover:border-red-400/50',
                      partial: isSelected ? 'bg-yellow-500/20 border-yellow-400 text-yellow-400 ring-2 ring-yellow-400/30' : 'bg-muted/30 border-border text-muted-foreground hover:border-yellow-400/50',
                      paid: isSelected ? 'bg-green-500/20 border-green-400 text-green-400 ring-2 ring-green-400/30' : 'bg-muted/30 border-border text-muted-foreground hover:border-green-400/50'
                    }
                    const iconMap = { pending: '⏳', partial: '💳', paid: '✅' }
                    return (
                      <button
                        key={status}
                        onClick={() => setEditingPaymentStatus(status)}
                        className={`flex-1 py-3 px-4 rounded-xl border text-xs font-black uppercase tracking-widest transition-all ${colorMap[status]}`}
                      >
                        <span className="mr-1.5">{iconMap[status]}</span>
                        {status}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-widest">
                      <th className="px-4 py-3">Product</th>
                      <th className="px-4 py-3 text-center">Qty</th>
                      <th className="px-4 py-3">Unit Cost</th>
                      <th className="px-4 py-3">Selling Price</th>
                      <th className="px-4 py-3">Line Total</th>
                      <th className="px-4 py-3">Expiry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {viewingGRN.items?.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-sm">{item.productName}</span>
                          {item.sku && <span className="text-xs text-muted-foreground ml-2">{item.sku}</span>}
                        </td>
                        <td className="px-4 py-3 text-center font-mono">{item.quantity}</td>
                        <td className="px-4 py-3">Rs.{(item.unitCost || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-emerald-400">Rs.{(item.sellingPrice || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 font-bold text-primary">Rs.{(item.lineTotal || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm">{item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Editable Notes */}
              <div className="mb-6">
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest mb-1">Notes</p>
                <textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  placeholder="Add notes about this GRN..."
                  rows={2}
                  className="w-full px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none resize-none text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => setViewingGRN(null)}
                  className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleUpdateGRN}
                  disabled={isUpdating}
                  className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors disabled:opacity-50"
                >
                  {isUpdating ? 'SAVING...' : 'SAVE CHANGES'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
